// =============================================================================
// LANGUAGE SWITCHER — i18n Language Selection Component
// =============================================================================
// Allows visitors to switch between available languages.
//
// BEST PRACTICES (from IDEA.md §6):
// - Uses TEXT LABELS in the native script ("Français", "Español", "中文")
//   instead of flags. Flags represent countries, not languages —
//   using a US flag for English alienates British/Australian/etc. users.
// - Positioned in the top corner of the header (via Navbar slot)
// - URL structure uses subdirectories (/en/, /fr/) for SEO
// =============================================================================

import './LanguageSwitcher.css';

/** Language definition */
export interface Language {
  /** ISO 639-1 code (e.g., "en", "fr", "es") */
  code: string;
  /** Display label in the native script (e.g., "Français", "Español") */
  label: string;
}

export interface LanguageSwitcherProps {
  /** Currently active language code */
  currentLocale: string;
  /** All available languages */
  languages: Language[];
  /** Callback when user selects a language */
  onLanguageChange: (locale: string) => void;
}

/**
 * Text-based language switcher (no flags by default).
 *
 * USAGE:
 *   <LanguageSwitcher
 *     currentLocale="en"
 *     languages={[
 *       { code: 'en', label: 'EN' },
 *       { code: 'fr', label: 'FR' },
 *     ]}
 *     onLanguageChange={(locale) => navigate(`/${locale}${path}`)}
 *   />
 */
export function LanguageSwitcher({
  currentLocale,
  languages,
  onLanguageChange,
}: LanguageSwitcherProps) {
  // Don't render if only one language is available
  if (languages.length <= 1) return null;

  return (
    <div className="lang-switcher" role="navigation" aria-label="Language selection">
      {languages.map((lang, index) => (
        <span key={lang.code}>
          {index > 0 && (
            <span className="lang-separator" aria-hidden="true">
              /
            </span>
          )}
          <button
            onClick={() => onLanguageChange(lang.code)}
            className={`lang-btn ${lang.code === currentLocale ? 'active' : ''}`}
            aria-current={lang.code === currentLocale ? 'true' : undefined}
            aria-label={`Switch to ${lang.label}`}
            type="button"
          >
            {lang.label}
          </button>
        </span>
      ))}
    </div>
  );
}
